import React, { useCallback } from 'react';
import { motion } from 'framer-motion';
import useEmblaCarousel from 'embla-carousel-react';
import { ChevronLeft, ChevronRight, ShoppingCart, Check, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useCart } from '../../contexts/CartContext';
import { toast } from 'sonner';

const BestsellerSection = ({ products, loading }) => {
  const { addToCart } = useCart();
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: false, align: 'start' });

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  const handleAddToCart = (product, e) => {
    e.preventDefault();
    e.stopPropagation();
    addToCart(product);
    toast.success(`${product.name} added to cart!`, {
      description: `₹${product.price.toLocaleString('en-IN')}`,
    });
  };

  const categoryLabels = {
    ghee: 'Pure Ghee',
    dal: 'Organic Dal',
    rudraksha: 'Rudraksha',
    bracelets: 'Sacred Bracelet',
    yantra: 'Sacred Yantra',
    keeda_jadi: 'Himalayan Herb',
    ganga_jal: 'Holy Water',
    other: 'Divine Product'
  };

  const productList = Array.isArray(products) ? products : (products?.products || []);

  return (
    <section className="py-12 sm:py-20 px-4 sm:px-6 lg:px-12 bg-[#FBF4E7] border-t border-[#E7D9BC]">
      <div className="max-w-7xl mx-auto">
        
        {/* Section Header */}
        <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-8 sm:mb-12">
          <div>
            <span className="text-xs font-bold uppercase tracking-widest text-[#B85A15]">
              HANDPICKED FOR YOUR SACRED SPACE
            </span>
            <h2 className="font-serif-heading text-2xl sm:text-4xl md:text-5xl font-bold text-[#4E1225] mt-1.5">
              Bestseller Picks
            </h2>
            <p className="text-[#83725B] mt-1.5 text-xs sm:text-base max-w-lg">
              Discover the divine products loved by our community, sourced directly from sacred Himalayan shrines.
            </p>
          </div>

          {/* Carousel Arrows */}
          <div className="flex items-center space-x-2.5 mt-4 sm:mt-0 self-end sm:self-auto">
            <button
              onClick={scrollPrev}
              className="w-9 h-9 sm:w-10 sm:h-10 rounded-full border border-[#E7D9BC] bg-[#FBF4E7] text-[#6E1B34] flex items-center justify-center hover:bg-[#6E1B34] hover:text-[#FFFFFF] transition-colors"
              aria-label="Previous product"
            >
              <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5" />
            </button>
            <button
              onClick={scrollNext}
              className="w-9 h-9 sm:w-10 sm:h-10 rounded-full border border-[#E7D9BC] bg-[#FBF4E7] text-[#6E1B34] flex items-center justify-center hover:bg-[#6E1B34] hover:text-[#FFFFFF] transition-colors"
              aria-label="Next product"
            >
              <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5" />
            </button>
          </div>
        </div>

        {/* Carousel / Grid Area */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6">
            {Array(4).fill(0).map((_, i) => (
              <div key={i} className="bg-[#F3E7D0] rounded-2xl h-80 animate-pulse" />
            ))}
          </div>
        ) : productList.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-2xl border border-[#E7D9BC] p-6">
            <p className="text-sm text-[#83725B]">No products currently available.</p>
          </div>
        ) : (
          <div className="overflow-hidden" ref={emblaRef}>
            <div className="flex space-x-4 sm:space-x-6">
              {productList.map((product, index) => (
                <div
                  key={product.id}
                  className="flex-[0_0_90%] sm:flex-[0_0_46%] md:flex-[0_0_30%] lg:flex-[0_0_23.5%] min-w-0"
                >
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.05 }}
                    className="group bg-[#FFFFFF] rounded-2xl overflow-hidden border border-[#E7D9BC] shadow-xs hover:shadow-md hover:border-[#B98B3E] transition-all duration-300 h-full flex flex-col justify-between"
                  >
                    <Link to={`/store/${product.slug || product.id}`} className="block flex-1 flex flex-col justify-between">
                      {/* Image Area */}
                      <div className="relative h-52 bg-[#F3E7D0] overflow-hidden">
                        <img
                          src={product.image_url}
                          alt={product.name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                        
                        {/* Ribbon / Origin */}
                        <div className="absolute top-2.5 left-2.5 sm:top-3 sm:left-3">
                          <span className="px-2.5 py-0.5 sm:py-1 bg-[#D9711F] text-white text-[10px] sm:text-[11px] font-bold uppercase tracking-wide rounded-md shadow-xs">
                            {product.origin || 'Vedic Sourced'}
                          </span>
                        </div>

                        {/* Category Label */}
                        <div className="absolute top-2.5 right-2.5 sm:top-3 sm:right-3">
                          <span className="px-2.5 py-0.5 sm:py-1 bg-[#FBF4E7]/95 text-[#4E1225] text-[11px] sm:text-xs font-semibold rounded-full border border-[#E7D9BC]">
                            {categoryLabels[product.category] || 'Divine'}
                          </span>
                        </div>
                      </div>

                      {/* Content */}
                      <div className="p-4 sm:p-5 flex-grow flex flex-col justify-between space-y-3">
                        <div>
                          <h3 className="font-serif-heading text-sm sm:text-base font-bold text-[#4E1225] line-clamp-1 group-hover:text-[#D9711F] transition-colors">
                            {product.name}
                          </h3>
                          <p className="text-xs text-[#83725B] mt-1 line-clamp-2 leading-relaxed">
                            {product.description}
                          </p>
                        </div>

                        <div className="flex items-center space-x-1.5 text-[11px] sm:text-xs text-[#2E7D45] font-semibold">
                          <Check className="w-3.5 h-3.5 flex-shrink-0" />
                          <span>100% Authentic &amp; Energised</span>
                        </div>

                        {/* Price & Action */}
                        <div className="pt-3 border-t border-[#E7D9BC] flex items-center justify-between">
                          <div>
                            <span className="text-[10px] text-[#83725B] block">Price</span>
                            <span className="font-price text-base sm:text-lg font-bold text-[#4E1225]">
                              ₹{product.price.toLocaleString('en-IN')}
                            </span>
                          </div>

                          <button
                            onClick={(e) => handleAddToCart(product, e)}
                            className="px-3.5 py-2 rounded-xl bg-[#6E1B34] text-[#FFFFFF] hover:bg-[#4E1225] transition-colors text-xs font-bold flex items-center space-x-1.5 shadow-xs active:scale-95"
                          >
                            <ShoppingCart className="w-3.5 h-3.5 text-[#E8D5A8]" />
                            <span>Add to Cart</span>
                          </button>
                        </div>
                      </div>
                    </Link>
                  </motion.div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* View All CTA */}
        <div className="text-center mt-8 sm:mt-12">
          <Link to="/store">
            <button className="px-6 sm:px-8 py-2.5 sm:py-3 rounded-xl bg-[#F3E7D0] border border-[#E7D9BC] text-[#6E1B34] font-bold text-xs sm:text-sm hover:bg-[#6E1B34] hover:text-[#FFFFFF] transition-colors uppercase tracking-wider inline-flex items-center space-x-2 shadow-xs">
              <span>Explore Complete Divine Collection</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </Link>
        </div>

      </div>
    </section>
  );
};

export default BestsellerSection;
